import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const EmailVerificationForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('Invalid verification token');
        setIsLoading(false);
        return;
      }

      console.log('Starting email verification with token:', token);
      
      // Debug API configuration
      console.log('=== API Debug Info ===');
      console.log('Current URL:', window.location.href);
      console.log('Window config:', window.__APP_CONFIG__);
      console.log('Build-time env vars:', {
        VITE_API_URL: import.meta.env.VITE_API_URL,
        REACT_APP_API_URL: import.meta.env.REACT_APP_API_URL
      });
      
      // Import getApiUrl to check the actual API URL being used
      const { getApiUrl } = await import('../../utils/config');
      const apiUrl = getApiUrl();
      console.log('Computed API URL:', apiUrl);
      console.log('Full verification URL:', `${apiUrl}/api/v1/auth/verify-email`);
      console.log('=== End API Debug Info ===');

      try {
        console.log('Making API call to verify email...');
        const response = await api.post('/auth/verify-email', { token });
        console.log('Verification successful:', response.data);
        setIsSuccess(true);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { message: 'Email verified successfully! You can now sign in.' }
          });
        }, 3000);
      } catch (err: any) {
        console.error('Verification failed:', err);
        console.error('Error response:', err.response);
        console.error('Error code:', err.code);
        console.error('Error message:', err.message);
        
        let errorMessage = 'Failed to verify email. The link may be invalid or expired.';
        
        if (err.code === 'ERR_NETWORK') {
          errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-sm w-full text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-medium text-gray-900 mb-2">
            Verifying your email
          </h1>
          <p className="text-gray-600 text-sm">
            Please wait while we verify your email address...
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-sm w-full text-center">
          <div className="mb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-medium text-gray-900 mb-2">
              Email verified!
            </h1>
            <p className="text-gray-600 text-sm mb-4">
              Your email has been successfully verified. You will be redirected to the sign in page shortly.
            </p>
            <p className="text-gray-500 text-xs">
              Redirecting in 3 seconds...
            </p>
          </div>

          <Link
            to="/login"
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            Continue to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="max-w-sm w-full text-center">
        <div className="mb-8">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-medium text-gray-900 mb-2">
            Verification failed
          </h1>
          <p className="text-gray-600 text-sm">
            {error}
          </p>
        </div>

        <div className="space-y-3">
          <Link
            to="/login"
            className="block w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 text-sm"
          >
            Go to sign in
          </Link>
          <Link
            to="/register"
            className="block text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            Create a new account
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationForm;